/**
 * stage.js — how far along a learner is.
 *
 * Mirrors `visionry-api/stage.js`. Keep the two in sync: these values are
 * written by the WhatsApp signup chain (router.js WAITLIST_QUESTIONS) and this
 * dashboard only ever displays or backfills what a student could actually have
 * been asked.
 *
 * Ireland and GB express the same idea in incompatible terms, so they get
 * separate columns:
 *   IE → `edt_progress`, the RSA's 12-lesson EDT scheme
 *   GB → `lesson_stage`, no mandated lesson count, so the useful split is
 *        distance-from-test rather than lessons-completed
 *
 * Exactly one is populated per row, decided by the instructor's `country`.
 */

export const EDT_LABEL = {
  'none': 'EDT not started', '1-3': '1–3 EDT done', '4-8': '4–8 EDT done',
  '9-11': '9–11 EDT done', 'complete': 'EDT done · pretest'
};

export const STAGE_LABEL = {
  'beginner': 'Complete beginner', 'some': 'Had some lessons',
  'test_booked': 'Test booked', 'retaking': 'Retaking test'
};

export const EDT_OPTIONS = [
  ['none', 'Not started yet'], ['1-3', '1–3 done'], ['4-8', '4–8 done'],
  ['9-11', '9–11 done'], ['complete', 'All 12 done']
];

export const STAGE_OPTIONS = [
  ['beginner', 'Complete beginner'], ['some', 'Had some lessons'],
  ['test_booked', 'Test booked'], ['retaking', 'Retaking test']
];

// Lesson types differ by market: '12 EDT' is not something a UK instructor
// offers, and an intensive course is not an Irish product.
export const LESSON_TYPE_OPTIONS_IE = [
  ['12 EDT', '12 EDT lessons'], ['Reduced EDT', 'Reduced EDT'],
  ['Pretest', 'Pretest lesson'], ['Car hire', 'Car hire (test day)']
];

export const LESSON_TYPE_OPTIONS_GB = [
  ['Weekly lessons', 'Weekly lessons'], ['Intensive course', 'Intensive course'],
  ['Pretest', 'Mock test / refresher'], ['Car hire', 'Car hire (test day)']
];

// Ireland is the default: every instructor predating migration 014 is Irish,
// and a missing country must never silently produce the wrong scheme.
export const isIE = (instructor) => (instructor?.country || 'IE') === 'IE';

// True once someone is close enough to a test that pretest/car-hire is the
// realistic ask — worth highlighting, since it's the most urgent group.
export const isPretestStage = (row, instructor) => isIE(instructor)
  ? row?.edt_progress === 'complete'
  : ['test_booked', 'retaking'].includes(row?.lesson_stage);

// Render a row's stage without knowing its country: whichever column is set.
// Returns null when unanswered, so callers can skip the chip entirely.
export function stageLabel(row) {
  if (!row) return null;
  if (row.edt_progress) return EDT_LABEL[row.edt_progress] || row.edt_progress;
  if (row.lesson_stage) return STAGE_LABEL[row.lesson_stage] || row.lesson_stage;
  return null;
}

// Amber marks the urgent end of either scheme; teal is everyone else.
export function stageTone(row) {
  if (!row) return 'teal';
  if (row.edt_progress === 'complete') return 'amber';
  if (['test_booked', 'retaking'].includes(row.lesson_stage)) return 'amber';
  return 'teal';
}
